import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Grid3X3, 
  List, 
  Eye, 
  Volume2,
  Play,
  Pause,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  User,
  X,
  Star,
  StarOff
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import SEO from './SEO';
import { getSEOConfig } from '../utils/seoConfig';
import '../styles/AudioArtworksPublicPage.css';

const AudioArtworksPublicPage = () => {
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [featuredFilter, setFeaturedFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDuration, setAudioDuration] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [updatingFeatured, setUpdatingFeatured] = useState({});
  const audioRefs = useRef({});

  const itemsPerPage = 20;

  // Check if user is admin
  const checkAdminStatus = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Check admin access using user_roles table (same as App.jsx)
        const { data: userRoles, error } = await supabase
          .from('user_roles')
          .select('*')
          .eq('user_id', user.id);

        if (!error && userRoles?.some(role => role.role_id === 3)) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  }, []);


  // Helper function to format time
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch artworks with audio
  const fetchArtworks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try Edge Function first, fallback to direct query
      try {
        const { data, error } = await supabase.functions.invoke('get_artworks_with_audio', {
          body: {
            page: currentPage,
            limit: itemsPerPage,
            search: searchTerm,
            sortBy,
            sortOrder,
            isPublic: true, // Public view - only show public artworks
            featured: featuredFilter
          }
        });

        if (error) throw error;

        if (data.success) {
          setArtworks(data.data);
          setTotalPages(data.pagination.totalPages);
          setTotalCount(data.pagination.total);
          return;
        }
      } catch (funcError) {
        console.warn('Edge function failed, using direct query:', funcError);
      }

      // Fallback: Direct Supabase query
      const offset = (currentPage - 1) * itemsPerPage;
      
      // First, get artwork IDs that have audio clips
      const { data: audioArtworkIds, error: audioError } = await supabase
        .from('media_elements')
        .select('vc_id')
        .eq('type', 'Audio')
        .eq('status', 'Public');

      if (audioError) {
        throw audioError;
      }

      if (!audioArtworkIds || audioArtworkIds.length === 0) {
        setArtworks([]);
        setTotalPages(0);
        setTotalCount(0);
        return;
      }

      const artworkIds = audioArtworkIds.map(item => item.vc_id);
      
      // Build query for artworks using vcs table
      let query = supabase
        .from('vcs')
        .select(`
          vc_id, title, description, cover_image, price, width, height, depth, access_control, created_at, updated_at, uuid,
          users!inner(uuid, nickname, first_name, last_name, handle)
        `)
        .in('vc_id', artworkIds)
        .eq('access_control', 'Public')
        .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%');

      // Add search filter
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,users.nickname.ilike.%${searchTerm}%,users.handle.ilike.%${searchTerm}%`);
      }

      // Add sorting
      if (sortBy === 'price') {
        query = query.order('price', { ascending: sortOrder === 'asc' });
      } else if (sortBy === 'title') {
        query = query.order('title', { ascending: sortOrder === 'asc' });
      } else if (sortBy === 'collection_count') {
        query = query.order('collection_count', { ascending: sortOrder === 'asc' });
      } else {
        query = query.order('created_at', { ascending: sortOrder === 'asc' });
      }

      // Add pagination
      query = query.range(offset, offset + itemsPerPage - 1);

      const { data: artworks, error: artworksError, count } = await query;

      if (artworksError) {
        throw artworksError;
      }



      // Get total count
      const { count: totalCount } = await supabase
        .from('vcs')
        .select('*', { count: 'exact', head: true })
        .in('vc_id', artworkIds)
        .eq('access_control', 'Public')
        .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%');

      // Fetch audio clips for all artworks
      const { data: allAudioClips, error: audioClipsError } = await supabase
        .from('media_elements')
        .select('*')
        .eq('type', 'Audio')
        .eq('status', 'Public')
        .in('vc_id', artworks?.map(a => a.vc_id) || []);

      if (audioClipsError) {
        console.error('Audio clips error:', audioClipsError);
      }

      // Group audio clips by artwork ID
      const audioClipsByArtwork = (allAudioClips || []).reduce((acc, clip) => {
        if (!acc[clip.vc_id]) {
          acc[clip.vc_id] = [];
        }
        acc[clip.vc_id].push(clip);
        return acc;
      }, {});

      // Debug logging
      console.log('Audio clips found:', allAudioClips?.length || 0);
      console.log('Audio clips by artwork:', audioClipsByArtwork);
      console.log('Artwork IDs:', artworks?.map(a => a.vc_id));

      // Process the data to include audio information
      const processedArtworks = artworks?.map(artwork => {
        const audioClips = audioClipsByArtwork[artwork.vc_id] || [];
        const user = artwork.users?.[0] || {}; // Get first user from joined data
        
        // Check if any audio clip is featured
        const hasFeaturedAudio = audioClips.some((clip) => clip.featured === true);
        
        console.log(`Artwork ${artwork.vc_id} has ${audioClips.length} audio clips`);
        console.log('User data for artwork:', artwork.title, user);
        console.log('Available user fields:', Object.keys(user));
        
        const artistName = user.nickname || 
                          `${user.first_name || ''} ${user.last_name || ''}`.trim() || 
                          user.handle || 'Unknown Artist';
        console.log('Computed artist name:', artistName);
        
        return {
          ...artwork,
          id: artwork.vc_id, // Map vc_id to id for consistency
          audio_clips: audioClips,
          audio_count: audioClips.length,
          has_audio: audioClips.length > 0,
          has_featured_audio: hasFeaturedAudio,
          artist_name: artistName,
          collection_count: 0 // Default value, can be fetched separately if needed
        };
      }) || [];

      setArtworks(processedArtworks);
      setTotalPages(Math.ceil((totalCount || 0) / itemsPerPage));
      setTotalCount(totalCount || 0);

    } catch (err) {
      console.error('Error fetching artworks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, sortBy, sortOrder, featuredFilter]);

  // Toggle featured status for an audio clip
  const toggleFeatured = useCallback(async (audioClipId, currentFeatured) => {
    const updateKey = `${audioClipId}`;
    setUpdatingFeatured(prev => ({ ...prev, [updateKey]: true }));
    
    try {
      const { error } = await supabase
        .from('media_elements')
        .update({ featured: !currentFeatured })
        .eq('element_id', audioClipId);
      
      if (error) throw error;
      
      // Refresh the artworks to update the UI
      await fetchArtworks();
    } catch (error) {
      console.error('Error updating featured status:', error);
      alert('Failed to update featured status');
    } finally {
      setUpdatingFeatured(prev => ({ ...prev, [updateKey]: false }));
    }
  }, [fetchArtworks]);

  useEffect(() => {
    fetchArtworks();
    checkAdminStatus();
  }, [fetchArtworks, checkAdminStatus]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchArtworks();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Audio controls
  const toggleAudio = useCallback((artworkId, audioIndex) => {
    const audioKey = `${artworkId}-${audioIndex}`;
    const audio = audioRefs.current[audioKey];
    
    if (!audio) return;

    if (playingAudio === audioKey) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      // Pause any currently playing audio
      Object.values(audioRefs.current).forEach(a => a.pause());
      
      audio.play();
      setPlayingAudio(audioKey);
    }
  }, [playingAudio]);

  const handleAudioRef = useCallback((artworkId, audioIndex, ref) => {
    if (ref) {
      audioRefs.current[`${artworkId}-${audioIndex}`] = ref;
    }
  }, []);

  const handleAudioEnded = useCallback(() => {
    setPlayingAudio(null);
  }, []);

  const handleTimeUpdate = useCallback((artworkId, audioIndex) => {
    const audioKey = `${artworkId}-${audioIndex}`;
    const audio = audioRefs.current[audioKey];
    if (audio) {
      setAudioProgress(prev => ({
        ...prev,
        [audioKey]: audio.currentTime
      }));
    }
  }, []);

  const handleLoadedMetadata = useCallback((artworkId, audioIndex) => {
    const audioKey = `${artworkId}-${audioIndex}`;
    const audio = audioRefs.current[audioKey];
    if (audio) {
      setAudioDuration(prev => ({
        ...prev,
        [audioKey]: audio.duration
      }));
    }
  }, []);

  const handleProgressClick = useCallback((artworkId, audioIndex, event) => {
    const audioKey = `${artworkId}-${audioIndex}`;
    const audio = audioRefs.current[audioKey];
    if (audio) {
      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const width = rect.width;
      const duration = audio.duration;
      const newTime = (clickX / width) * duration;
      
      audio.currentTime = newTime;
      setAudioProgress(prev => ({
        ...prev,
        [audioKey]: newTime
      }));
    }
  }, []);

  // Lightbox functions
  const handleArtworkClick = (artwork) => {
    navigate(`/artwork/${artwork.id}`);
  };

  const openLightbox = (artwork) => {
    setSelectedArtwork(artwork);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
    setPlayingAudio(null);
  };

  // Utility functions
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDimensions = (width, height, depth) => {
    let dimensions = `${width}"W × ${height}"H`;
    if (depth && depth > 0) {
      dimensions += ` × ${depth}"D`;
    }
    return dimensions;
  };

  // Pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>
      </div>
    );
  };

  if (loading && artworks.length === 0) {
    return (
      <div className="audio-artworks-public">
        <SEO {...getSEOConfig['audio-artworks']()} />
        <div className="loading-container">
          <RefreshCw className="loading-spinner" />
          <p>Loading audio-enhanced artworks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audio-artworks-public">
        <SEO {...getSEOConfig['audio-artworks']()} />
        <div className="error-container">
          <h2>Error Loading Artworks</h2>
          <p>{error}</p>
          <button onClick={fetchArtworks} className="retry-btn">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-artworks-public">
      <SEO {...getSEOConfig['audio-artworks']()} />
      
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-title">
            <Volume2 size={24} />
            <h1>ArtSpeaks</h1>
          </div>
          <p className="hero-subtitle">
            Experience art through sound. Discover artworks with audio explanations, 
            music, and artist insights that bring each piece to life.
          </p>
        </div>
      </div>

      <div className="search-section">
        <div className="search-container">
          <div className="search-input-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder={`Search ${totalCount} artworks`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="controls-container">
          <div className="filter-controls">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={featuredFilter}
                onChange={(e) => setFeaturedFilter(e.target.checked)}
              />
              <span className="checkmark"></span>
              Featured Audio Only
            </label>
          </div>
          
          <div className="sort-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="created_at">Date Created</option>
              <option value="title">Title</option>
              <option value="price">Price</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode('grid')}
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              title="Grid View"
            >
              <Grid3X3 size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              title="List View"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="artworks-section">
        <div className={`artworks-container ${viewMode === 'list' ? 'list' : 'grid'}`}>
          {artworks.length === 0 ? (
            <div className="empty-state">
              <Volume2 size={48} />
              <h3>No Audio-Enhanced Artworks Found</h3>
              <p>No artworks with audio clips match your current search criteria.</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="artwork-table">
              <div className="table-header">
                <div className="header-cell">Image</div>
                <div className="header-cell">Title</div>
                <div className="header-cell">Artist</div>
                <div className="header-cell">Audio</div>
              </div>
              {artworks.map((artwork) => (
                <div key={artwork.id} className="artwork-row" onClick={() => handleArtworkClick(artwork)}>
                  <div className="cell-image">
                    <img
                      src={artwork.cover_image || '/images/placeholder-artwork.jpg'}
                      alt={artwork.title}
                    />
                  </div>
                  <div className="cell-title">{artwork.title}</div>
                  <div className="cell-artist">{artwork.artist_name}</div>
                  <div className="cell-audio">
                    <div className="audio-preview-list">
                      {artwork.audio_clips?.slice(0, 1).map((audio, index) => {
                        const audioKey = `${artwork.id}-${index}`;
                        const isPlaying = playingAudio === audioKey;
                        const currentTime = audioProgress[audioKey] || 0;
                        const duration = audioDuration[audioKey] || 0;
                        const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
                        
                        return (
                          <div key={index} className="audio-player-list">
                            <div className="audio-player-header-list">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAudio(artwork.id, index);
                                }}
                                className="audio-play-btn-list"
                              >
                                {isPlaying ? (
                                  <Pause size={14} />
                                ) : (
                                  <Play size={14} />
                                )}
                              </button>
                              <div className="audio-info-list">
                                <span className="audio-title-list">
                                  {audio.title || `Audio ${index + 1}`}
                                </span>
                                <div className="audio-time-list">
                                  {formatTime(currentTime)} / {formatTime(duration)}
                                </div>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFeatured(audio.element_id, audio.featured);
                                  }}
                                  className={`featured-toggle-btn-list ${audio.featured ? 'featured' : ''}`}
                                  disabled={updatingFeatured[audio.element_id]}
                                  title={audio.featured ? 'Remove from featured' : 'Mark as featured'}
                                >
                                  {updatingFeatured[audio.element_id] ? (
                                    <RefreshCw size={12} className="spinning" />
                                  ) : audio.featured ? (
                                    <Star size={12} />
                                  ) : (
                                    <StarOff size={12} />
                                  )}
                                </button>
                              )}
                            </div>
                            
                            <div className="audio-progress-container-list">
                              <div 
                                className="audio-progress-bar-list"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProgressClick(artwork.id, index, e);
                                }}
                              >
                                <div 
                                  className="audio-progress-fill-list"
                                  style={{ width: `${progressPercent}%` }}
                                />
                                <div 
                                  className="audio-progress-handle-list"
                                  style={{ left: `${progressPercent}%` }}
                                />
                              </div>
                            </div>
                            
                            <audio
                              ref={(ref) => handleAudioRef(artwork.id, index, ref)}
                              src={audio.source}
                              onEnded={handleAudioEnded}
                              onTimeUpdate={() => handleTimeUpdate(artwork.id, index)}
                              onLoadedMetadata={() => handleLoadedMetadata(artwork.id, index)}
                              preload="metadata"
                            />
                          </div>
                        );
                      })}
                      {artwork.audio_count > 1 && (
                        <div className="more-audio-list">
                          <Volume2 size={12} />
                          <span>+{artwork.audio_count - 1} more</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="artworks-grid">
              {artworks.map((artwork) => (
                <div key={artwork.id} className="artwork-card-new" onClick={() => handleArtworkClick(artwork)}>
                  <div className="artwork-image-container">
                    <img
                      src={artwork.cover_image || '/images/placeholder-artwork.jpg'}
                      alt={artwork.title}
                      className="artwork-image"
                      onClick={() => openLightbox(artwork)}
                    />
                    <div className="audio-indicator">
                      <Volume2 size={16} />
                      <span>{artwork.audio_count}</span>
                    </div>
                    {artwork.has_featured_audio && (
                      <div className="featured-badge">
                        Featured
                      </div>
                    )}
                  </div>

                  <div className="artwork-info">
                    <h3 className="artwork-title">{artwork.title}</h3>
                    <p className="artwork-artist">{artwork.artist_name}</p>
                    
                    {artwork.price && (
                      <p className="artwork-price">{formatPrice(artwork.price)}</p>
                    )}
                    
                    {artwork.width && artwork.height && (
                      <p className="artwork-dimensions">
                        {formatDimensions(artwork.width, artwork.height, artwork.depth)}
                      </p>
                    )}

                    <div className="artwork-audio-info">
                      <Volume2 size={16} />
                      <span>{artwork.audio_count} audio clip{artwork.audio_count !== 1 ? 's' : ''}</span>
                    </div>

                    {/* Audio Preview */}
                    <div className="audio-preview">
                      {artwork.audio_clips?.slice(0, 2).map((audio, index) => {
                        const audioKey = `${artwork.id}-${index}`;
                        const isPlaying = playingAudio === audioKey;
                        const currentTime = audioProgress[audioKey] || 0;
                        const duration = audioDuration[audioKey] || 0;
                        const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
                        
                        return (
                          <div key={index} className="audio-player">
                            <div className="audio-player-header">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAudio(artwork.id, index);
                                }}
                                className="audio-play-btn"
                              >
                                {isPlaying ? (
                                  <Pause size={16} />
                                ) : (
                                  <Play size={16} />
                                )}
                              </button>
                              <div className="audio-info">
                                <span className="audio-title">
                                  {audio.title || `Audio ${index + 1}`}
                                </span>
                                <div className="audio-time">
                                  {formatTime(currentTime)} / {formatTime(duration)}
                                </div>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFeatured(audio.element_id, audio.featured);
                                  }}
                                  className={`featured-toggle-btn ${audio.featured ? 'featured' : ''}`}
                                  disabled={updatingFeatured[audio.element_id]}
                                  title={audio.featured ? 'Remove from featured' : 'Mark as featured'}
                                >
                                  {updatingFeatured[audio.element_id] ? (
                                    <RefreshCw size={14} className="spinning" />
                                  ) : audio.featured ? (
                                    <Star size={14} />
                                  ) : (
                                    <StarOff size={14} />
                                  )}
                                </button>
                              )}
                            </div>
                            
                            <div className="audio-progress-container">
                              <div 
                                className="audio-progress-bar"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProgressClick(artwork.id, index, e);
                                }}
                              >
                                <div 
                                  className="audio-progress-fill"
                                  style={{ width: `${progressPercent}%` }}
                                />
                                <div 
                                  className="audio-progress-handle"
                                  style={{ left: `${progressPercent}%` }}
                                />
                              </div>
                            </div>
                            
                            <audio
                              ref={(ref) => handleAudioRef(artwork.id, index, ref)}
                              src={audio.source}
                              onEnded={handleAudioEnded}
                              onTimeUpdate={() => handleTimeUpdate(artwork.id, index)}
                              onLoadedMetadata={() => handleLoadedMetadata(artwork.id, index)}
                              preload="metadata"
                            />
                          </div>
                        );
                      })}
                      {artwork.audio_count > 2 && (
                        <div className="more-audio">
                          <Volume2 size={14} />
                          <span>+{artwork.audio_count - 2} more audio clips</span>
                        </div>
                      )}
                    </div>

                    <div className="artwork-actions">
                      <button
                        onClick={(e) => { e.stopPropagation(); openLightbox(artwork); }}
                        className="action-btn primary"
                        title="View artwork details"
                      >
                        <Eye size={16} />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && renderPagination()}
      </div>

      {/* Lightbox */}
      {lightboxOpen && selectedArtwork && (
        <div className="artwork-modal" onClick={closeLightbox}>
          <div className="artwork-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="artwork-modal-header">
              <h3>{selectedArtwork.title}</h3>
              <button onClick={closeLightbox}>×</button>
            </div>
            <div className="artwork-modal-body">
              <div className="artwork-modal-image">
                <img 
                  src={selectedArtwork.cover_image || '/images/placeholder-artwork.jpg'} 
                  alt={selectedArtwork.title}
                />
              </div>
              <div className="artwork-modal-details">
                <p><strong>Artist:</strong> {selectedArtwork.artist_name}</p>
                {selectedArtwork.width && selectedArtwork.height && (
                  <p><strong>Dimensions:</strong> {formatDimensions(selectedArtwork.width, selectedArtwork.height, selectedArtwork.depth)}</p>
                )}
                {selectedArtwork.price && <p><strong>Price:</strong> {formatPrice(selectedArtwork.price)}</p>}
                {selectedArtwork.medium && <p><strong>Medium:</strong> {selectedArtwork.medium}</p>}
                {selectedArtwork.description && <p><strong>Description:</strong> {selectedArtwork.description}</p>}
                <p><strong>Created:</strong> {formatDate(selectedArtwork.created_at)}</p>
                <p><strong>Collections:</strong> {selectedArtwork.collection_count} collections</p>
                <p><strong>Audio Clips:</strong> {selectedArtwork.audio_count} clips</p>
                
                {/* Audio Section */}
                <div className="audio-section">
                  <h4>Audio Clips</h4>
                  <div className="audio-clips-list">
                    {selectedArtwork.audio_clips?.map((audio, index) => (
                      <div key={index} className="audio-clip-item">
                        <button
                          onClick={() => toggleAudio(selectedArtwork.id, index)}
                          className="audio-play-btn-large"
                        >
                          {playingAudio === `${selectedArtwork.id}-${index}` ? (
                            <Pause size={20} />
                          ) : (
                            <Play size={20} />
                          )}
                        </button>
                        <div className="audio-info">
                          <span className="audio-title">
                            {audio.title || `Audio Clip ${index + 1}`}
                          </span>
                          <span className="audio-date">
                            {formatDate(audio.created_at)}
                          </span>
                        </div>
                        <audio
                          ref={(ref) => handleAudioRef(selectedArtwork.id, index, ref)}
                          src={audio.source}
                          onEnded={handleAudioEnded}
                          preload="none"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="artwork-engagement-details">
                  <h4>Actions</h4>
                  <div className="collection-actions">
                    <button 
                      className="view-details-btn"
                      onClick={() => {
                        navigate(`/artwork/${selectedArtwork.vc_id}`);
                        closeLightbox();
                      }}
                      title="View full artwork page"
                    >
                      <Eye size={16} />
                      View Full Page
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioArtworksPublicPage;
